import { PlusOutlined, WarningOutlined } from '@ant-design/icons';
import { ProductDoc } from '@gooduncles/gu-app-schema';
import { Button, Image, Modal, Skeleton, message } from 'antd';
import { FC, useState } from 'react';

import useSWRProduct from 'src/hooks/swr/useSWRProduct';
import useSWRProductInventory from 'src/hooks/swr/useSWRProductInventory';

import Warning from 'src/atomic-components/atoms/TextItem/Warning';
import AlgoliaSearch from 'src/atomic-components/molecules/AlgoriaSearch/AlgoriaSearch';

type SearchedProduct = {
  productId: string;
  productFullName: string;
};

interface InventoryProductAddingModalProps {
  onAdd: (product: ProductDoc) => void;
}

const InventoryProductAddingModal: FC<InventoryProductAddingModalProps> = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [searchedProduct, setSearchedProduct] = useState<SearchedProduct>();
  const { product, isLoading } = useSWRProduct(searchedProduct?.productId ?? null);
  const { inventory, isLoading: isLoadingInventory } = useSWRProductInventory(product?.inventoryId ?? null);

  const handleProductSearch = (productId: string, productFullName: string) => {
    setSearchedProduct({ productId, productFullName });
  };

  const handleOk = () => {
    if (!product) {
      message.error('상품을 찾을 수 없습니다.');
      return;
    }
    onAdd(product);
    setOpen(false);
    setSearchedProduct(undefined);
  };

  const handleCancel = () => {
    setOpen(false);
    setSearchedProduct(undefined);
  };

  return (
    <>
      <Button size='small' icon={<PlusOutlined />} onClick={() => setOpen(true)}>
        추가
      </Button>
      <Modal
        open={open}
        width={420}
        title='상품 추가'
        onCancel={handleCancel}
        onOk={handleOk}
        okText='추가'
        cancelText='취소'
        confirmLoading={isLoading}
        okButtonProps={{ disabled: !searchedProduct }}>
        <AlgoliaSearch placeholder='상품코드 또는 상품명으로 검색' onSearch={handleProductSearch} />
        {searchedProduct && (
          <>
            {isLoading ? (
              <Skeleton active />
            ) : (
              <>
                {product ? (
                  <>
                    <div className='flexRow flexAlignCenter' style={{ padding: '8px 0' }}>
                      <Image
                        width={40}
                        height={40}
                        src={product.thumbnail ?? undefined}
                        style={{ borderRadius: 4, border: '1px solid var(--gray200)' }}
                      />
                      <span>{product.fullName}</span>
                    </div>
                    {isLoadingInventory ? (
                      <Skeleton active />
                    ) : (
                      <>
                        {inventory && (
                          <>
                            <Warning
                              icon={<WarningOutlined style={{ color: 'var(--red400)', fontSize: 18, padding: 6 }} />}>
                              이미 연결된 재고 <b>&quot;{inventory.inventoryName}&quot;</b>가 있습니다.
                              <br />
                              등록을 완료하면 기존 재고와의 연결은 해제됩니다.
                            </Warning>
                          </>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <p>상품을 찾을 수 없습니다.</p>
                )}
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default InventoryProductAddingModal;
